import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { CHANNEL_MEDIA_PLAN_STATUS } from '@skyrise-eng/types';
import { useDeleteMediaPlanMutation, useLazyGetCampaignSchedulePDFQuery } from '@/api';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useRole } from '@/hooks/useRole';
import { APP_SCOPE, NAMESPACE, PRIVATE_PATH, DELETE_ACTION_COLOR } from '@/constants';
import type { MenuItems, UploadPDF, UseActions } from '../Actions.types';
import { useModal } from '@/hooks/useModal';
import { buildPDF } from '@/utils';
import { auth } from '@/services/firebase';

async function downloadExcelFile(args: {
  mediaPlanId: string;
  budgets?: number[];
  token: string;
  filenameOverride?: string;
}) {
  const { mediaPlanId, budgets, token, filenameOverride } = args;
  const url = new URL(`excel/api/v1/media-plan/${mediaPlanId}`, import.meta.env.VITE_API_URL + '/');
  if (budgets && budgets.length) {
    url.searchParams.set('budgets', budgets.join(','));
  }

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Excel export failed (${res.status}): ${text}`);
  }

  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  const filename = filenameOverride?.trim() || match?.[1] || `media-plan-${mediaPlanId}.xlsx`;

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

function toSafeFilename(value: string): string {
  // Windows disallows: \ / : * ? " < > |
  // Also strip control characters & collapse whitespace.
  return value
    .replace(/[\\/:*?"<>|]/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const useActions: UseActions = ({ id, mediaPlan }) => {
  const { canCreate, canDelete, canUpdate } = useRole(APP_SCOPE.MEDIA_PLAN);
  const { isOpen: isEditMediaPlanFormOpen, open, close: closeEditMediaPlanForm } = useModal();
  const [deleteMediaPlan, { isLoading: isDeleting, isSuccess: isDeletionSuccessful }] =
    useDeleteMediaPlanMutation();
  const [getCampaignSchedulePDF, { isLoading: isPdfLoading }] = useLazyGetCampaignSchedulePDFQuery();
  const navigate = useNavigate();
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const { handleConfirm: handleDeleteMediaPlan } = useConfirmModal({
    title: t('deleteMediaPlanModalTitle'),
    content: t('deleteMediaPlanModalDescription'),
    destructive: true,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('delete', { ns: NAMESPACE.COMMON }),
    onCancel: () => deleteMediaPlan(id)
  });

  useEffect(() => {
    if (isDeletionSuccessful) {
      message.success(t('mediaPlanHasBeenDeleted'));
      navigate(PRIVATE_PATH.MEDIA_PLAN);
    }
  }, [navigate, isDeletionSuccessful, t]);

  const uploadPDF: UploadPDF = useCallback(
    async (ev) => {
      ev.stopPropagation();
      console.log('[PDF] Download clicked', { id, campaignName: mediaPlan?.campaign?.name });
      try {
        const result = await getCampaignSchedulePDF(id);
        const { data, error } = result;

        if (error) {
          const errMsg =
            (error as { data?: { message?: string } })?.data?.message ??
            (error as { message?: string })?.message ??
            t('pdfDownloadFailed');
          console.error('[PDF] API error:', error);
          message.error(errMsg);
          return;
        }

        if (data && mediaPlan?.campaign.name) {
          await buildPDF(mediaPlan.campaign.name, data);
        } else if (!data) {
          console.error('[PDF] No data returned from API');
          message.error(t('pdfDownloadFailed'));
        } else if (!mediaPlan?.campaign.name) {
          console.error('[PDF] Missing campaign name');
          message.error(t('pdfDownloadFailed'));
        }
      } catch (err) {
        console.error('[PDF] Error:', err);
        message.error(t('pdfDownloadFailed'));
      }
    },
    [getCampaignSchedulePDF, id, mediaPlan?.campaign.name, t]
  );

  const downloadIOPdf = useCallback(async () => {
    try {
      const result = await getCampaignSchedulePDF(id);
      const { data, error } = result;

      if (error) {
        const errMsg =
          (error as { data?: { message?: string } })?.data?.message ??
          (error as { message?: string })?.message ??
          t('pdfDownloadFailed');
        message.error(errMsg);
        return;
      }

      if (data && mediaPlan?.campaign.name) {
        await buildPDF(mediaPlan.campaign.name, data);
      } else {
        message.error(t('pdfDownloadFailed'));
      }
    } catch (err) {
      message.error(t('pdfDownloadFailed'));
    }
  }, [getCampaignSchedulePDF, id, mediaPlan?.campaign.name, t]);

  const canDownloadExcel = true;

  const downloadExcelCurrent = useCallback(async () => {
    if (!canDownloadExcel) return;
    setIsExcelLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const campaignName = mediaPlan?.campaign?.name ? toSafeFilename(mediaPlan.campaign.name) : `media-plan-${id}`;
      const filenameOverride = `${campaignName} - Media Plan.xlsx`;
      await downloadExcelFile({
        mediaPlanId: id,
        token,
        filenameOverride
      });
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      message.error(detail || 'Excel export failed');
    } finally {
      setIsExcelLoading(false);
    }
  }, [canDownloadExcel, id, mediaPlan?.campaign?.name]);

  const downloadExcelScenarios = useCallback(
    async (budgets: number[]) => {
      if (!canDownloadExcel) return;
      setIsExcelLoading(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error('Not authenticated');

        const campaignName = mediaPlan?.campaign?.name ? toSafeFilename(mediaPlan.campaign.name) : `media-plan-${id}`;
        const filenameOverride = `${campaignName} - Media Plan.xlsx`;
        await downloadExcelFile({
          mediaPlanId: id,
          budgets,
          token,
          filenameOverride
        });
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        message.error(detail || 'Excel export failed');
      } finally {
        setIsExcelLoading(false);
      }
    },
    [canDownloadExcel, id, mediaPlan?.campaign?.name]
  );

  const menuItems: MenuItems = useMemo(() => {
    const items: MenuItems = [];

    if (canUpdate) {
      items.unshift({
        label: t('edit'),
        onClick: open,
        key: 3
      });
    }

    if (canDelete) {
      items.push({
        disabled: isDeleting,
        label: createElement('span', { style: { color: DELETE_ACTION_COLOR } }, t('delete', { ns: 'common' })),
        onClick: () => handleDeleteMediaPlan(),
        danger: true,
        key: 4
      });
    }

    return items;
  }, [t, canUpdate, canDelete, open, isDeleting, handleDeleteMediaPlan]);

  return {
    menuItems,
    isEditMediaPlanFormOpen,
    closeEditMediaPlanForm,
    uploadPDF,
    downloadIOPdf,
    isPdfLoading,
    canUploadPdf: mediaPlan?.status === CHANNEL_MEDIA_PLAN_STATUS.READY && canCreate,
    downloadExcelCurrent,
    downloadExcelScenarios,
    canDownloadExcel,
    isExcelLoading
  };
};
